import React, { useState, useCallback, useEffect, useRef } from "react";
import { useParams, useHistory, useLocation, Prompt } from "react-router";
import { Row, Col, Typography, Divider, notification, Button } from "antd";
import { CheckCircleFilled } from "@ant-design/icons";
import Style from "./BookingSummary.module.scss";
import { useWindowSize } from "../../../hooks/useWindowSize";
import {
  GeneralDefinition,
  WandaBookingStatus,
} from "../../../types/ApplicationTypes";
import { getServiceUserInfoForDashBoard } from "../../../services";
import { useTripsCtx } from "../../../context/TripDetailsContext";
import {
  IDownloadPdfTrips,
  IDownloadPdfTrip,
  ITripInvoiceEmail,
} from "../../../types/DownloadPdfTypes";
import DownloadPdf from "../DownloadPdf/DownloadPdf";
import GoogleLoader from "../../../assets/Loader/GoogleSvgLoader/GoogleLoader";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { usePaginationCtx } from "../../../context/PaginationContext";
import BrowserBackButtonPopup from "../BrowserBackButtonPopup/BrowserBackButtonPopup";
import { usePlanDetailsCtx } from "../../../context/PlanDetailsContext";
import { usePersonSummaryCtx } from "../../../context/PersonSummaryContext";
import { getPaymentInstrumentByPaymentInstrumentId } from "../../../services/PaymentService/PaymentInstrumentService";
import { CreditCardDetails } from "../../../types/PaymentTypes";
import { PaymentSubscriptionStatus } from "../../../types/SubscriptionTypes";
import moment from "moment";
import { useDashboardCtx } from "../../../context/DashboardContext";

interface IParams {
  bookingId: string;
}

const BookingSummary: React.FC = () => {
  const [creditCardDetails, setCreditCardDetails] = useState<
    CreditCardDetails
  >();
  const history = useHistory();
  const { width } = useWindowSize();
  const {
    isLoading,
    mapBase64Ctx,
    tripScheduleDateCtx,
    bookedTripPaymentInstrumentIdCtx,
    tripSetupDetailsCtx,
    wandaTripInfoCtx,
  } = useTripsCtx();

  const [wandaTripInfo] = wandaTripInfoCtx;
  const [tripCreationDetails] = tripSetupDetailsCtx;
  const [tripScheduleDate] = tripScheduleDateCtx;
  const { caregiverSummaryInfo } = usePersonSummaryCtx();
  const { resetPagination, selectedPersonIdCtx } = useDashboardCtx();
  const [selectedPersonId, setSelectedPersonId] = selectedPersonIdCtx;
  const { bookingId } = useParams<IParams>();
  const [data, setData] = useState<IDownloadPdfTrips>([]);
  const [tripsData, setTripsData] = useState<IDownloadPdfTrips>([]);
  const [emailBlob, setEmailBlob] = useState<Blob>();
  const [mapBase64] = mapBase64Ctx;
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const shouldBlockNavigation = useRef(true);
  const [isBrowserBackButtonPopup, setIsBrowserBackButtonPopup] = useState<
    boolean
  >(false);
  const [bookedTripPaymentInstrumentId] = bookedTripPaymentInstrumentIdCtx;

  const {
    tripDetailsInfo,
    currentPlanDetails,
    fetchTripsAfterSubscriptionDate,
  } = usePlanDetailsCtx();
  const redirectToDashboard = () => {
    if (
      currentPlanDetails?.paymentSubscriptionStatusId ===
      PaymentSubscriptionStatus.Active
    ) {
      const tripDate = moment(tripScheduleDate);
      const subscriptionValidityDate = moment(currentPlanDetails?.validUntil);
      tripDate.isAfter(subscriptionValidityDate) &&
        fetchTripsAfterSubscriptionDate(currentPlanDetails?.validUntil);
    }
    resetPagination();
    history.push("/DashBoard");
    setSelectedPersonId(selectedPersonId);
  };

  const handleBackToDashboard = () => redirectToDashboard();

  const wandaPlusDiscountTotalCost = () => {
    let totalCost = 0;

    if (tripCreationDetails && Array.isArray(tripCreationDetails)) {
      for (let i = 0; i < tripCreationDetails.length; i++) {
        totalCost +=
          tripCreationDetails[i]?.legCostDetails?.wandaPlusDiscountCost ?? 0;
      }
    }
    if (tripCreationDetails[0]?.isPlus) {
      return totalCost;
    } else {
      return 0;
    }
  };

  const wandaPromoCodeDiscountTotalCost = () => {
    let totalCost = 0;

    if (tripCreationDetails && Array.isArray(tripCreationDetails)) {
      for (let i = 0; i < tripCreationDetails.length; i++) {
        totalCost +=
          tripCreationDetails[i]?.legCostDetails?.discountAmount ?? 0;
      }
    }
    return totalCost;
  };
  const tripGrantTotal = tripCreationDetails[0]?.grantTotal ?? 0;
  const wandaDiscountTotal = wandaPlusDiscountTotalCost();
  const promoCodeDiscountTotal = wandaPromoCodeDiscountTotalCost();
  const tripTotalSum =
    tripGrantTotal + wandaDiscountTotal + promoCodeDiscountTotal;

  const handleBrowserActions = (location: any, action: string) => {
    if (action === "POP" && location.pathname !== "/DashBoard/EditProfile") {
      shouldBlockNavigation.current = false;
      setIsBrowserBackButtonPopup(true);
      return false;
    }
    return true;
  };

  const handleCancelBrowserBackButtonPopup = () =>
    setIsBrowserBackButtonPopup(false);

  const handleOkBrowserBackButtonPopup = () => redirectToDashboard();

  useEffect(() => {
    (async () => {
      try {
        const ccInfo = await getPaymentInstrumentByPaymentInstrumentId(
          bookedTripPaymentInstrumentId
        );

        setCreditCardDetails(ccInfo);
      } catch (error) {
        notification.error({
          message: "Error",
          description: "Failed to Fetch Credit Card Details",
        });
      }
    })();
  }, []);

  useEffect(() => {
    const secheduleDate = query.get("scheduleDate");
    const endDate = query.get("endDate");

    if (secheduleDate && endDate) {
      const fetchPdfData = async () => {
        const tripRecords = await getServiceUserInfoForDashBoard(
          caregiverSummaryInfo.personId,
          secheduleDate,
          endDate
        );
        if (tripRecords.length === 0) {
          notification.error({
            message: "Error",
            description: "No Records found on this user.",
          });
        }

        const currentTrips: IDownloadPdfTrip = tripRecords.find(
          (item: IDownloadPdfTrip) => item.bookingId === bookingId
        );
        if (currentTrips && Object.keys(currentTrips).length > 0) {
          const trips: IDownloadPdfTrips = tripRecords
            .filter(
              (item: IDownloadPdfTrip) =>
                item.requestAssignmentId === currentTrips.requestAssignmentId
            )
            .sort((a: IDownloadPdfTrip, b: IDownloadPdfTrip) =>
              a.bookingId.localeCompare(b.bookingId)
            );
          for (const [i, item] of trips.entries()) {
            let pdfMapImageData: string = mapBase64[i];
            trips[i].pdfMapImageData = pdfMapImageData;
            trips[i].statusDetail = WandaBookingStatus.Booked;
          }
          setData(trips);
          setTripsData(trips);
        } else {
          notification.error({
            message: "Error",
            description: "Can't find the bookingId on this trip.",
          });
        }
      };

      fetchPdfData();
    } else {
      notification.error({
        message: "Error",
        description: "Start and end date are missing. please check.",
      });
    }
  }, [caregiverSummaryInfo.personId, bookingId]);

  const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64data = reader.result as string;
        resolve(base64data.split(",")[1]);
      };
      reader.onerror = () => {
        reject(new Error("Error converting Blob to Base64"));
      };
      reader.readAsDataURL(blob);
    });
  };

  return (
    <>
      {data && data.length > 0 && !isLoading ? (
        <>
          <Prompt
            when={!isBrowserBackButtonPopup}
            message={handleBrowserActions}
          />
          <BrowserBackButtonPopup
            isBrowserBackButtonPopup={isBrowserBackButtonPopup}
            handleCancelBrowserBackButtonPopup={
              handleCancelBrowserBackButtonPopup
            }
            handleOkBrowserBackButtonPopup={handleOkBrowserBackButtonPopup}
          />

          {width > GeneralDefinition.MobileView ? (
            <Row>
              <Col span={24}>
                <Row justify="center" className={Style.rowContainer}>
                  <Col>
                    <CheckCircleFilled
                      className={Style.SummarySuccessIcon}
                      translate=""
                    />
                  </Col>
                </Row>
                <Row justify="center" className={Style.rowContainer}>
                  <Col>
                    <Typography.Text className={Style.SummaryTitle}>
                      Your service has been booked!
                    </Typography.Text>
                  </Col>
                </Row>
                <Row justify="center" className={Style.rowContainer}>
                  <Col>
                    <Typography.Text id={Style.SummaryBookingIdTitle}>
                      Your Booking ID is <b>{bookingId}.</b>
                    </Typography.Text>
                  </Col>
                </Row>
                <Row justify="center" className={Style.rowContainer}>
                  <Col>
                    <Typography.Text className={Style.SummaryCreditCardTitle}>
                      Your credit card will be charged after each trip is
                      complete.
                    </Typography.Text>
                  </Col>
                </Row>
                <Row justify="center" className={Style.rowContainer}>
                  <Col span={6}>
                    <Divider dashed className={Style.SummaryBoarder} />
                  </Col>
                </Row>
                <Row justify="center" className={Style.rowContainer}>
                  <Col>
                    <Typography.Text className={Style.SummaryCreditCardTitle}>
                      Your booking summary has been sent to:
                    </Typography.Text>
                  </Col>
                </Row>
                <Row justify="center" className={Style.rowContainer}>
                  <Col>
                    <Typography.Text className={Style.SummaryEmailIdTitle}>
                      {caregiverSummaryInfo && caregiverSummaryInfo.wandaEmail}
                    </Typography.Text>
                  </Col>
                </Row>
                <Row justify="center" className={Style.pdfDownloadContainer}>
                  <Col>
                    <PDFDownloadLink
                      document={
                        <DownloadPdf
                          bookingId={bookingId}
                          wandaCustomerCareNumber={
                            tripDetailsInfo.wandaCustomerCareNumber
                          }
                          paymentInfo={creditCardDetails}
                          wandaTripInfo={wandaTripInfo}
                          tripTotalCost={tripTotalSum}
                          wandaDiscountTotalCost={wandaPlusDiscountTotalCost}
                          wandaPromoCodeTotalCost={
                            wandaPromoCodeDiscountTotalCost
                          }
                          data={data}
                        />
                      }
                      fileName="Trip Invoice.pdf"
                    >
                      {({ blob, url, loading, error }) => {
                        if (loading) {
                          return <GoogleLoader />;
                        }
                        if (blob) {
                          setEmailBlob(blob);
                        }
                        return (
                          <Button id={Style.buttonTheme}>Download PDF</Button>
                        );
                      }}
                    </PDFDownloadLink>
                  </Col>
                </Row>
                <Row
                  justify="center"
                  className={Style.backToDashboardContainer}
                >
                  <Col>
                    <Button
                      onClick={handleBackToDashboard}
                      className={Style.buttonThemeBlue}
                      id={Style.ButtonTextBackToDashboard}
                    >
                      Back to Dashboard
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          ) : (
            <Row>
              <Col span={24}>
                <Row justify="center" className={Style.rowContainer}>
                  <Col>
                    <CheckCircleFilled
                      className={Style.SummarySuccessIcon}
                      translate=""
                    />
                  </Col>
                </Row>
                <Row justify="center" className={Style.pdfDownloadContainer}>
                  <Col>
                    <Typography.Text className={Style.SummaryTitle}>
                      Your service has been
                      <span className={Style.newRowTextCenter}>booked!</span>
                    </Typography.Text>
                  </Col>
                </Row>
                <Row justify="center" className={Style.pdfDownloadContainer}>
                  <Col>
                    <Typography.Text id={Style.SummaryBookingIdTitleMobile}>
                      Your Booking ID is <b>{bookingId}.</b>
                    </Typography.Text>
                  </Col>
                </Row>
                <Row justify="center" className={Style.rowContainer}>
                  <Col>
                    <Typography.Text
                      className={Style.SummaryCreditCardTitleMobile}
                    >
                      Your credit card will be charged
                      <span className={Style.newRowTextCenter}>
                        after each trip is complete.
                      </span>
                    </Typography.Text>
                  </Col>
                </Row>
                <Row justify="center" className={Style.rowContainer}>
                  <Col span={20}>
                    <Divider dashed className={Style.SummaryBoarder} />
                  </Col>
                </Row>
                <Row justify="center" className={Style.rowContainer}>
                  <Col>
                    <Typography.Text
                      className={Style.SummaryCreditCardTitleMobile}
                    >
                      Your booking summary has been sent to:
                    </Typography.Text>
                  </Col>
                </Row>
                <Row justify="center" className={Style.rowContainer}>
                  <Col>
                    <Typography.Text className={Style.SummaryEmailIdTitle}>
                      {caregiverSummaryInfo && caregiverSummaryInfo.wandaEmail}
                    </Typography.Text>
                  </Col>
                </Row>
                <Row justify="center" className={Style.pdfDownloadContainer}>
                  <Col>
                    <PDFDownloadLink
                      document={
                        <DownloadPdf
                          bookingId={bookingId}
                          wandaCustomerCareNumber={
                            tripDetailsInfo.wandaCustomerCareNumber
                          }
                          paymentInfo={creditCardDetails}
                          wandaTripInfo={wandaTripInfo}
                          tripTotalCost={tripTotalSum}
                          wandaDiscountTotalCost={wandaPlusDiscountTotalCost}
                          wandaPromoCodeTotalCost={
                            wandaPromoCodeDiscountTotalCost
                          }
                          data={data}
                        />
                      }
                      fileName="Trip Invoice.pdf"
                    >
                      {({ blob, url, loading, error }) => {
                        if (loading) {
                          return <GoogleLoader />;
                        }
                        if (blob) {
                          setEmailBlob(blob);
                        }
                        return (
                          <Button id={Style.buttonTheme}>Download PDF</Button>
                        );
                      }}
                    </PDFDownloadLink>
                  </Col>
                </Row>
                <Row justify="center" className={Style.rowContainer}>
                  <Col span={20}>
                    <Divider dashed className={Style.SummaryBoarder} />
                  </Col>
                </Row>
                <Row
                  justify="center"
                  className={Style.backToDashboardContainer}
                >
                  <Col>
                    <Button
                      onClick={handleBackToDashboard}
                      className={Style.buttonThemeBlue}
                    >
                      Back to Dashboard
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          )}
        </>
      ) : (
        <GoogleLoader />
      )}
    </>
  );
};

export default BookingSummary;
